import React, { useEffect, useState } from 'react';
import { Col, Row, Table } from 'antd';
import {
  AppContent,
  ErrorHandler,
  Icon,
  LoadingState,
  SecondaryButton,
  Link,
} from '@common/components';
import { Colors } from '@common/tokens';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { useParams, useNavigate, useQuery } from '@common/hooks';
import {
  getApprovalChainTitle,
  getChainingColumnsQuantity,
  getLetterSignByNumber,
  getSortCallbackBy,
  getTransformedDataSource,
} from '@approvals/services/approvals-service';
import {
  getColumnRenderObj,
  renderApprovalChainColumn,
  renderValueWithRowSpan,
} from './_approvals-page-render-service';
import { ApprovalsPageLocationSelect } from './_approvals-page-location-select';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import {
  APPROVALS_ORGANIZATION_BUSINESS_QUERY,
  APPROVALS_ORGANIZATION_BUSINESSES_QUERY,
  ApprovalTableRowModelType,
} from '@approvals/graphql';
import { ColumnsType } from 'antd/lib/table';
import { iterationMap } from 'works/legacy/services/iteration-service';
import { buildDialogUrl } from '@common/services';
import { ApprovalsEditDrawerTrigger } from '@approvals/components';

export const ApprovalsPage = () => {
  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: ORGANIZATION_ADMIN_MENU.APPROVALS.LABEL,
      path: ORGANIZATION_ADMIN_MENU.APPROVALS.ROUTE,
    },
  ]);

  const navigate = useNavigate();
  const { organizationBusinessId } = useParams<{
    organizationBusinessId?: string;
  }>();

  const approvalsConfigsDrawerUrl = buildDialogUrl('approvals-options-drawer');

  const [selectedOrganizationBusiness, setSelectedOrganizationBusiness] =
    useState({
      id: '',
      isDirty: false,
    });

  const [isSortingEnabled, setIsSortingEnabled] = useState(false);
  const [, forceUpdateForTooltipRecalc] = useState({});

  useEffect(() => {
    // Append the organization business id to the url in case it is not present
    if (selectedOrganizationBusiness.id && !organizationBusinessId) {
      navigate(`/works/admin/approvals/${selectedOrganizationBusiness.id}`);
    }
  }, [selectedOrganizationBusiness, organizationBusinessId, navigate]);

  const {
    loading: organizationBusinessOptionsLoading,
    error: organizationBusinessOptionsError,
    data: organizationBusinessOptionsData,
  } = useQuery(APPROVALS_ORGANIZATION_BUSINESSES_QUERY);

  const {
    loading: organizationBusinessLoading,
    error: organizationBusinessError,
    data: organizationBusinessData,
  } = useQuery(APPROVALS_ORGANIZATION_BUSINESS_QUERY, {
    skip: !selectedOrganizationBusiness.id,
    variables: {
      id: selectedOrganizationBusiness.id,
    },
    onCompleted() {
      forceUpdateForTooltipRecalc({});
    },
  });

  const organizationBusinessOptions =
    organizationBusinessOptionsData?.organizationBusinesses;

  useEffect(() => {
    if (
      !organizationBusinessOptions?.length ||
      selectedOrganizationBusiness.isDirty
    ) {
      return;
    }

    const firstIdOfList = organizationBusinessOptions?.[0].id;

    setSelectedOrganizationBusiness({
      isDirty: true,
      id: organizationBusinessId || firstIdOfList || '',
    });
  }, [
    organizationBusinessOptions,
    selectedOrganizationBusiness,
    organizationBusinessId,
  ]);

  const anyError =
    organizationBusinessError || organizationBusinessOptionsError;

  if (anyError) {
    return <ErrorHandler error={anyError} log="ApprovalsPage" />;
  }

  if (organizationBusinessOptionsLoading) {
    return <LoadingState />;
  }

  const { organizationUnitsActive = [] } =
    organizationBusinessData?.organizationBusiness || {};

  const approvalChainColumnsQuantity = getChainingColumnsQuantity(
    organizationUnitsActive
  );

  const selectedOrganizationBusinessModel = organizationBusinessOptions?.find(
    (organizationBusinessOption) =>
      organizationBusinessOption.id === selectedOrganizationBusiness.id
  );
  const transformedDataSource = getTransformedDataSource(
    organizationUnitsActive,
    selectedOrganizationBusinessModel
  );

  const columns: ColumnsType<ApprovalTableRowModelType> = [
    {
      title: 'Unit',
      dataIndex: 'unitName',
      key: 'unit-name',
      width: '200px',
      fixed: 'left',
      sorter: getSortCallbackBy<ApprovalTableRowModelType>('unitName'),
      render: renderValueWithRowSpan(transformedDataSource),
    },
    {
      title: 'Cost Center',
      dataIndex: 'costCenter',
      key: 'cost-center',
      width: '100px',
      fixed: 'left',
      sorter: getSortCallbackBy<ApprovalTableRowModelType>('costCenter'),
      render: renderValueWithRowSpan(transformedDataSource),
    },
    {
      title: 'Type',
      dataIndex: 'actionType',
      key: 'action-type',
      width: '160px',
      fixed: 'left',
      render: (_: unknown, row: ApprovalTableRowModelType) => {
        const renderObj = getColumnRenderObj(row);
        renderObj.children = (
          <ApprovalsEditDrawerTrigger
            organizationBusinessName={row.organizationBusinessName}
            unitId={row.unitId}
            actionTypeKey={row.actionTypeKey}
          />
        );
        return renderObj;
      },
    },
  ];

  iterationMap(approvalChainColumnsQuantity, (i) => {
    const letter = getLetterSignByNumber(i);

    columns.push({
      title: getApprovalChainTitle(letter),
      dataIndex: `approvalsChain${letter}`,
      key: `approvals-chain-${letter}`,
      width: '274px',
      render: renderApprovalChainColumn(i),
      fixed: undefined,
    });
  });

  return (
    <>
      <AppContent className="approvals-page">
        <Row
          justify="space-between"
          align="bottom"
          style={{ paddingBottom: 16 }}
        >
          <Col>
            <ApprovalsPageLocationSelect
              options={organizationBusinessOptions}
              value={selectedOrganizationBusiness.id}
              onChange={(value) =>
                setSelectedOrganizationBusiness({
                  ...selectedOrganizationBusiness,
                  id: value,
                })
              }
            />
          </Col>
          <Col>
            <Link to={approvalsConfigsDrawerUrl}>
              <SecondaryButton
                size="large"
                icon={<Icon icon="sliders" kind="light" />}
              >
                Options
              </SecondaryButton>
            </Link>
          </Col>
        </Row>
        <Row>
          <Col>
            <Table
              className={`approvals-page__main-table ${
                isSortingEnabled && 'approvals-page__main-table--sorted'
              }`}
              scroll={{ x: 'scroll' }}
              loading={organizationBusinessLoading}
              pagination={false}
              columns={columns}
              onChange={(a, b, sorter) => {
                const changes = [sorter].flat();

                setIsSortingEnabled(changes.some((change) => !!change.order));
              }}
              dataSource={transformedDataSource}
            />
          </Col>
        </Row>
      </AppContent>
      <style jsx>
        {`
          :global(
            .approvals-page .ant-table-tbody > tr.ant-table-row:hover > td
          ) {
            background: ${Colors.white};
          }
          :global(
            .approvals-page
              .approvals-page__main-table:not(
                .approvals-page__main-table--sorted
              )
              .ant-table-tbody
              > tr.ant-table-row
              > td.table-odd-row:not(.ant-table-column-sort)
          ) {
            background: ${Colors.steelLightest};
          }
          :global(.approvals-page .ant-table-tbody > tr > td) {
            padding-top: 16px;
            vertical-align: top;
          }
          :global(.approvals-page__approval-chain-list) {
            max-width: 250px;
          }
          ,
          :global(.approvals-page__approval-chain-list),
          :global(.approvals-page__tooltip-step-list) {
            list-style: none;
            padding-left: 0;
            margin: 0;
          }
          :global(.approvals-page__approval-chain-list li) {
            margin-bottom: 8px;
            font-size: 13px;
          }
          :global(.approvals-page__step-sign) {
            font-size: 11px;
            border-radius: 4px;
            border: 1px solid ${Colors.midnightLightest};
            color: gray;
            min-width: 29px;
            display: inline-block;
            text-align: center;
            margin-right: 5px;
            line-height: 15px;
          }
        `}
      </style>
    </>
  );
};
